<template>
    <div>
        <div class="relative flex ">
            <form-label ref="textLabel" class="w-2/5 shrink-0 whitespace-nowrap" v-if="name"
                :class="scrolling && !no_scroll ? 'scrolling-text' : ''">
                {{ name }}
            </form-label>
            <div class="flex items-center min-w-0" :class="[name ? 'flex-1' : 'w-full']">
                <div class="relative flex-1 min-w-0" :class="{ 'slider-container': type === 'range' }"
                     :data-value="modelValue"
                     :style="type === 'range' ? `--slider-percent: ${sliderPercent}` : ''">
                    <input
                        class="w-full px-1 text-gray-900 appearance-none placeholder-opacity-100 placeholder-gray-500 rounded focus:outline-none"
                        :class="{
                            'border border-gray-400': type !== 'range',
                            'range-slider': type === 'range',
                            'border-red-500 bg-red-100': isInvalid,
                            'bg-gray-200 text-gray-400 cursor-not-allowed': input_disabled,
                            'focus:border-blue-500': !readonly && type !== 'range',
                            'hover:border-gray-500': !input_disabled && type !== 'range'
                        }"
                        ref="input" @input="updateValue($event.target.value)" @focus="beginEdit"
                        @change="commitValue" @blur="commitValue" @keydown.enter.prevent="commitValue"
                        :disabled="input_disabled"
                        :readonly="readonly" :type="type" :min="min" :max="max" :step="step" :id="id" :name="name" :placeholder="placeholder"
                        :required="required" :value="modelValue" />
                </div>

                <input v-if="default_val" type="checkbox" class="ml-1 cursor-pointer" :checked="input_disabled"
                    :title="checkbox_tooltip" @change="toggleDisabled" />
            </div>
            <!-- Shared property status/reset column. Renders nothing unless the
                 caller opts in with :resettable, so every other use of this
                 input is unchanged. -->
            <property-override-indicator class="ml-1"
                :resettable="resettable" :modified="modified" :inherited="inherited"
                @reset="$emit('reset')" />

        </div>
        <p v-if="isInvalid" class="mt-1 text-red-500 italic">
            {{ errorMessage }}
        </p>
    </div>
</template>

<script>
import PropertyOverrideIndicator from '../properties/PropertyOverrideIndicator.vue';

export default {
    components: { PropertyOverrideIndicator },
    emits: ['update:modelValue', 'update:disabled', 'reset', 'commit'],
    props: {
        id: String,
        name: String,
        // Property override status — see PropertyOverrideIndicator.vue.
        resettable: { type: Boolean, default: null },
        modified: { type: Boolean, default: false },
        inherited: { type: Boolean, default: false },
        modelValue: {
            required: false,
            default: '',
        },
        required: {
            type: Boolean,
            default: false,
        },
        placeholder: {
            type: String,
            required: false,
            default: 'input',
        },
        type: {
            type: String,
            required: false,
            default: 'text',
        },
        disabled: {
            type: Boolean,
            default: false,
        },
        readonly: {
            type: Boolean,
            default: false,
        },
        min: [String, Number],
        max: [String, Number],
        step: [String, Number],
        default_val: {
            type: Boolean,
            required: false,
            default: false
        },
        checkbox_tooltip: {
            type: String,
            required: false,
            default: null
        },
        no_scroll: {
            type: Boolean,
            required: false,
            default: true
        },
        validator: {
            type: String,
            required: false,
            default: null,
            validator: (value) => ['strftime', 'number', null].includes(value)
        },
        completion: {
            type: Array,
            required: false,
            default: () => []
        }
    },
    data() {
        return {
            input_disabled: this.disabled,
            scrolling: false,
            highlightedIndex: -1,
            hasCommitted: false,
            committedValue: undefined,
        };
    },
    computed: {
        isInvalid() {
            return this.validator ? !this.validateInput(this.modelValue) : false;
        },
        errorMessage() {
            if (this.validator === 'strftime') return 'strftime format';
            if (this.validator === 'number') return 'number format';
            return '';
        },
        filteredSuggestions() {
            if (!this.modelValue || !this.completion.length) return [];
            return this.completion.filter(word =>
                word.toLowerCase().startsWith(this.modelValue.toLowerCase())
            );
        },
        sliderPercent() {
            if (this.type !== 'range') return 0;
            const min = parseFloat(this.min) || 0;
            const max = parseFloat(this.max) || 100;
            const value = parseFloat(this.modelValue) || 0;
            return ((value - min) / (max - min)) * 100;
        }
    },
    watch: {
        disabled(v) {
            this.input_disabled = v;
        },
    },
    methods: {
        updateValue(value) {
            this.hasCommitted = false;
            let retval = value;
            if (this.type === 'number' || this.type === 'range')
                retval = Number(value);
            this.$emit('update:modelValue', retval);
            this.highlightedIndex = -1;
        },
        beginEdit() {
            this.hasCommitted = false;
            this.scrolling = false;
        },
        commitValue() {
            if (this.hasCommitted && Object.is(this.committedValue, this.modelValue)) return;
            this.hasCommitted = true;
            this.committedValue = this.modelValue;
            this.$emit('commit', this.modelValue);
        },
        toggleDisabled() {
            this.input_disabled = !this.input_disabled;
            this.$emit("update:disabled", this.input_disabled);
        },
        validateInput(value) {
            if (!value) return true;

            switch (this.validator) {
                case 'strftime':
                    return this.validateStrftimeFormat(value);
                case 'number':
                    return !isNaN(value) && value.trim() !== '';
                default:
                    return true;
            }
        },
        validateStrftimeFormat(format) {
            const strftimeDirectives = /%[aAbBcCdDeFgGhHIjklmMnNpPrRsStTuUVwWxXyYzZ]/g;
            return strftimeDirectives.test(format);
        },
        // selectSuggestion(suggestion) {
        //     this.$emit('update:modelValue', suggestion);
        //     this.highlightedIndex = -1;
        //     filteredSuggestions = [];
        //     // Hide suggestions immediately
        //     // this.$nextTick(() => {
        //     //     this.filteredSuggestions = [];
        //     // });
        // },
        // hideSuggestions() {
        //     setTimeout(() => {
        //         this.filteredSuggestions = [];
        //     }, 500); // Short delay to allow click event
        // },
        // highlightNext() {
        //     if (this.filteredSuggestions.length > 0) {
        //         this.highlightedIndex = (this.highlightedIndex + 1) % this.filteredSuggestions.length;
        //     }
        // },
        // highlightPrevious() {
        //     if (this.filteredSuggestions.length > 0) {
        //         this.highlightedIndex =
        //             (this.highlightedIndex - 1 + this.filteredSuggestions.length) % this.filteredSuggestions.length;
        //     }
        // },
        // selectHighlighted() {
        //     if (this.highlightedIndex !== -1) {
        //         this.selectSuggestion(this.filteredSuggestions[this.highlightedIndex]);
        //     }
        // }
    }
};
</script>

<style>
.scrolling-text {
    animation: scrollText 5s linear infinite;
}

@keyframes scrollText {
    from {
        transform: translateX(0);
    }

    to {
        transform: translateX(-100%);
    }
}

/* Range slider styling */
.slider-container {
    position: relative;
}

.range-slider {
    -webkit-appearance: none;
    appearance: none;
    background: transparent;
    cursor: pointer;
    padding: 0;
}

/* Track - Chrome/Safari/Edge */
.range-slider::-webkit-slider-runnable-track {
    background: #d1d5db;
    height: 5px;
    border-radius: 3px;
}

/* Track - Firefox */
.range-slider::-moz-range-track {
    background: #d1d5db;
    height: 5px;
    border-radius: 3px;
}

/* Thumb/Knob - Chrome/Safari/Edge */
.range-slider::-webkit-slider-thumb {
    -webkit-appearance: none;
    appearance: none;
    margin-top: -6px;
    background-color: #3b82f6;
    height: 17px;
    width: 17px;
    border-radius: 50%;
    border: 2px solid #fff;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.3);
    position: relative;
}

/* Thumb/Knob - Firefox */
.range-slider::-moz-range-thumb {
    background-color: #3b82f6;
    height: 17px;
    width: 17px;
    border-radius: 50%;
    border: 2px solid #fff;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.3);
}

/* Value display tooltip above knob */
.slider-container::after {
    content: attr(data-value);
    position: absolute;
    top: -28px;
    left: calc(var(--slider-percent, 50) * 1%);
    transform: translateX(-50%);
    background: #1f2937;
    color: white;
    padding: 3px 8px;
    border-radius: 4px;
    font-size: 10px;
    font-weight: 600;
    pointer-events: none;
    opacity: 0;
    transition: opacity 0.2s ease;
    white-space: nowrap;
    z-index: 10;
}

.slider-container:hover::after,
.slider-container:focus-within::after {
    opacity: 1;
}

/* Hover effects */
.range-slider::-webkit-slider-thumb:hover {
    background-color: #2563eb;
}

.range-slider::-moz-range-thumb:hover {
    background-color: #2563eb;
}

/* Focus effects */
.range-slider:focus::-webkit-slider-thumb {
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.3);
}

.range-slider:focus::-moz-range-thumb {
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.3);
}

/* Disabled state */
.range-slider:disabled::-webkit-slider-thumb {
    background-color: #9ca3af;
    cursor: not-allowed;
}

.range-slider:disabled::-moz-range-thumb {
    background-color: #9ca3af;
    cursor: not-allowed;
}

.range-slider:disabled::-webkit-slider-runnable-track {
    background: #e5e7eb;
}

.range-slider:disabled::-moz-range-track {
    background: #e5e7eb;
}
</style>
